import axios from 'axios';

type RetryConfig = {
    maxRetries?: number;
    initialDelayMs?: number;
    maxDelayMs?: number;
    backoffMultiplier?: number;
    retryableStatusCodes?: number[];
};

const DEFAULT_RETRY_CONFIG: Required<RetryConfig> = {
    maxRetries: 3,
    initialDelayMs: 300,
    maxDelayMs: 3_000,
    backoffMultiplier: 2,
    retryableStatusCodes: [408, 429, 500, 502, 503, 504],
};

const sleep = async (ms: number): Promise<void> => {
    await new Promise<void>(resolve => {
        setTimeout(resolve, ms);
    });
};

function shouldRetry(error: unknown, retryableStatusCodes: number[]): boolean {
    if (!axios.isAxiosError(error)) {
        return false;
    }

    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
        return true;
    }

    const status = error.response?.status;
    return status !== undefined && retryableStatusCodes.includes(status);
}

function calculateDelay(
    attemptNumber: number,
    initialDelayMs: number,
    maxDelayMs: number,
    backoffMultiplier: number,
): number {
    const delay = initialDelayMs * backoffMultiplier ** attemptNumber;
    const jitter = Math.random() * 0.3 * delay;
    return Math.min(delay + jitter, maxDelayMs);
}

async function retryWithBackoff<T>(
    operation: () => Promise<T>,
    config: RetryConfig = {},
): Promise<T> {
    const { maxRetries, initialDelayMs, maxDelayMs, backoffMultiplier, retryableStatusCodes } = {
        ...DEFAULT_RETRY_CONFIG,
        ...config,
    };

    let lastError: unknown;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await operation();
        } catch (error) {
            lastError = error;

            if (attempt === maxRetries || !shouldRetry(error, retryableStatusCodes)) {
                throw error;
            }

            const delay = calculateDelay(attempt, initialDelayMs, maxDelayMs, backoffMultiplier);
            await sleep(delay);
        }
    }

    throw lastError;
}

export { type RetryConfig, retryWithBackoff };
